import { HashRouter, useLocation, useNavigate } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';

import { Button, Header, Modal } from 'components';

import { LayersProvider } from 'contexts/LayersContext';
import { OptionsProvider } from 'contexts/OptionsContext';
import { MapProvider } from 'contexts/MapContext';

import { Router } from 'routes';

import theme from 'styles/theme';
import GlobalStyles from 'styles/global';
import { FilterProvider } from 'contexts/FilterContext';
import { ToastContainer } from 'react-toastify';
import { TabelaAtributosProvider } from 'contexts/TabelaAtributosContext';
import { AuthProvider, clearStoredPreferences, useAuth } from 'contexts/AuthContext';
import { useEffect } from 'react';
import { finalizeMonitoringSession } from 'services/monitoring';

function Application() {
  const { acknowledgeSessionExpired, loading, preferencesLoaded, sessionExpired, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginRoute = location.pathname === '/login' || (location.pathname === '/' && !user);

  useEffect(() => {
    if (isLoginRoute && !user) {
      clearStoredPreferences();
      window.dispatchEvent(new CustomEvent('p3m:clearMapDisplay'));
    }
  }, [isLoginRoute, user]);

  useEffect(() => {
    if (isLoginRoute || sessionExpired) void finalizeMonitoringSession({ useBeacon: true });
  }, [isLoginRoute, sessionExpired]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!window.__isAuth) {
        clearStoredPreferences();
      }
      void finalizeMonitoringSession({ useBeacon: true });
    };
    const handlePageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) handleBeforeUnload();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);


  const closeSessionExpiredModal = () => {
    acknowledgeSessionExpired();
    clearStoredPreferences();
    window.dispatchEvent(new CustomEvent('p3m:clearMapDisplay'));
    navigate('/login', { replace: true });
  };
  if (loading || (user && !preferencesLoaded)) return <div />;

  return (
    <FilterProvider>
      <MapProvider>
        <LayersProvider>
          <OptionsProvider>
            <TabelaAtributosProvider>
              {!isLoginRoute && <Header />}
              <Router />
              {sessionExpired && (
                <Modal onDisabled={closeSessionExpiredModal}>
                  <div style={{ width: 'min(36rem, calc(100vw - 4.8rem))' }}>
                    <h2 style={{ margin: '0 0 1.2rem', fontSize: '2rem', color: '#282828' }}>Sessao expirada</h2>
                    <p style={{ margin: '0 0 2.4rem', fontSize: '1.6rem', lineHeight: 1.5, color: '#707070' }}>
                      Sua sessao expirou. Faca login novamente para continuar.
                    </p>
                    <Button type="button" onClick={closeSessionExpiredModal}>
                      OK
                    </Button>
                  </div>
                </Modal>
              )}
            </TabelaAtributosProvider>
          </OptionsProvider>
        </LayersProvider>
      </MapProvider>
    </FilterProvider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <HashRouter basename={process.env.PUBLIC_URL}>
          <ToastContainer position="top-right" autoClose={3000} />
          <Application />
          <GlobalStyles />
        </HashRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
